import { useTranslation } from 'react-i18next';
import { List } from '@/components/List';
import { usePermissions } from '@/hooks';
import { Permission } from '@/types/permissions';
import { SourceFolder } from '@/types/source-folder';
import { SourcesSelection } from '@/types/source';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { type FolderSortOption } from '../../hooks/useFolderSort';
import { AddFolderButton } from './AddFolderButton';
import { FolderGroup } from './FolderGroup';
import { LoadMoreButton } from './LoadMoreButton';

export interface SourcesFolderListProps {
    myFolders: SourceFolder[];
    sharedFolders: SourceFolder[];
    allSourcesCount: number;
    sharedSourcesCount: number;
    selection: SourcesSelection;
    onSelectionChange: (selection: SourcesSelection) => void;
    onCreateFolderClick: () => void;
    onEditFolder?: (folder: SourceFolder) => void;
    onDeleteFolder?: (folder: SourceFolder) => void;
    myFoldersSortOption?: FolderSortOption;
    onMyFoldersSortChange?: (option: FolderSortOption) => void;
    sharedFoldersSortOption?: FolderSortOption;
    onSharedFoldersSortChange?: (option: FolderSortOption) => void;
    isLoadingGroups?: boolean;
    hasMoreMyFolders?: boolean;
    onLoadMoreMyFolders?: () => void;
    isLoadingMoreMyFolders?: boolean;
    hasMoreSharedFolders?: boolean;
    onLoadMoreSharedFolders?: () => void;
    isLoadingMoreSharedFolders?: boolean;
    className?: string;
}

export function SourcesFolderList({
    myFolders,
    sharedFolders,
    allSourcesCount,
    sharedSourcesCount,
    selection,
    onSelectionChange,
    onCreateFolderClick,
    onEditFolder,
    onDeleteFolder,
    myFoldersSortOption,
    onMyFoldersSortChange,
    sharedFoldersSortOption,
    onSharedFoldersSortChange,
    isLoadingGroups,
    hasMoreMyFolders,
    onLoadMoreMyFolders,
    isLoadingMoreMyFolders,
    hasMoreSharedFolders,
    onLoadMoreSharedFolders,
    isLoadingMoreSharedFolders,
    className,
}: SourcesFolderListProps) {
    const { t } = useTranslation([I18nNamespace.SOURCES, I18nNamespace.COMMON]);
    const { has } = usePermissions();

    const canCreateFolder = has(Permission.CREATE);
    const selectedFolderId = selection.kind === 'folder' ? selection.id : null;

    const isSharedFoldersExpanded =
        selection.kind === 'organization' ||
        (selection.kind === 'folder' &&
            sharedFolders.some((folder) => folder.id === selection.id));

    return (
        <List className={cn('gap-4', className)}>
            <FolderGroup
                title={t('folders.mySources')}
                count={allSourcesCount}
                selected={selection.kind === 'private'}
                defaultExpanded
                loading={Boolean(isLoadingGroups)}
                folders={myFolders}
                selectedFolderId={selectedFolderId}
                onSelect={onSelectionChange}
                onSelectGroup={() => onSelectionChange({ kind: 'private' })}
                onEditFolder={onEditFolder}
                onDeleteFolder={onDeleteFolder}
                sortOption={myFoldersSortOption}
                onSortChange={onMyFoldersSortChange}
                addButton={
                    canCreateFolder ? (
                        <AddFolderButton
                            label={t('folders.createFolder')}
                            disabled={isLoadingGroups}
                            onClick={onCreateFolderClick}
                        />
                    ) : null
                }
                loadMore={
                    hasMoreMyFolders && onLoadMoreMyFolders ? (
                        <LoadMoreButton
                            label={t('folders.loadMore')}
                            disabled={isLoadingGroups || isLoadingMoreMyFolders}
                            onClick={onLoadMoreMyFolders}
                        />
                    ) : null
                }
            />
            {sharedFolders.length > 0 && (
                <FolderGroup
                    title={t('folders.sharedWithMe')}
                    count={sharedSourcesCount}
                    selected={selection.kind === 'organization'}
                    defaultExpanded={isSharedFoldersExpanded}
                    loading={Boolean(isLoadingGroups)}
                    folders={sharedFolders}
                    selectedFolderId={selectedFolderId}
                    onSelect={onSelectionChange}
                    onSelectGroup={() =>
                        onSelectionChange({ kind: 'organization' })
                    }
                    sortOption={sharedFoldersSortOption}
                    onSortChange={onSharedFoldersSortChange}
                    loadMore={
                        hasMoreSharedFolders && onLoadMoreSharedFolders ? (
                            <LoadMoreButton
                                label={t('folders.loadMore')}
                                disabled={
                                    isLoadingGroups ||
                                    isLoadingMoreSharedFolders
                                }
                                onClick={onLoadMoreSharedFolders}
                            />
                        ) : null
                    }
                />
            )}
        </List>
    );
}
